/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import Grid from "@mui/material/Grid";
import FormFader from "../Common/Fader/FormFader";
import { useLocation, useNavigate } from "react-router";
import Paper from "@mui/material/Paper";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import { dateFieldDefault, isRequired, isTodayOrPastDate, pastDateDefault, requiredValidator } from "../../../services/validationRules";
import DialogModal from "../../DialogModal/DialogModal";
import { useForm } from "../../../provider/Form/FormProvider";
import Person from "../Common/Person/Person";
import Address from "../Common/Address/Address";
import Contact from "../Common/Contact/Contact";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import ValidatorDateField from "../../FormFields/ValidatorDateField";
import { Box, Stack } from "@mui/material";
import { translate } from "../../../services/translations/translations";
import FormSubmitProps from "../../../types/FormSubmitProps";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import { FormDataTypes } from "../../../types/FormDataTypes";
import wizardStyle from "../wizardStyle";
import InfoIconButton from "../../Button/InfoIconButton";

export default function Petitioner({ product, submitProps }: { product: AbstractCase; submitProps: FormSubmitProps }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { handleSubmit, values }: FormDataTypes = useForm();

  const handleInfoModalOpen = () => {
    navigate(location.pathname, { state: { infoModalOpen: true } });
  };

  const handleModalClose = () => {
    navigate(-1);
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Persönliche Angaben
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            {product.authorityNotification === "fine_notice" && (
              <>
                <Grid sx={wizardStyle.gridTopSpaced} size={12}>
                  <FormLegend>{translate("traffic.fineNoticeReceivedDate.question", values)}</FormLegend>
                </Grid>
                <Grid size={10}>
                  <ValidatorDateField
                    name="fineNoticeReceivedDate"
                    label={translate("traffic.fineNoticeReceivedDate.label", values)}
                    validators={[
                      requiredValidator("Bitte geben Sie an, wann Sie den Bußgeldbescheid erhalten haben."),
                      dateFieldDefault,
                      { validator: isTodayOrPastDate, message: "Das Datum darf nicht in der Zukunft liegen." },
                    ]}
                  />
                </Grid>
                <Grid size={2}>
                  <Stack
                    sx={{
                      justifyContent: "center",
                      alignItems: "center",
                      height: "80px",
                    }}
                  >
                    <InfoIconButton onClick={handleInfoModalOpen} />
                  </Stack>
                  <DialogModal open={!!(location.state && location.state.infoModalOpen)} onClose={handleModalClose} title="Datum der Zustellung">
                    <p>
                      Wichtig: Hier geht es darum, wann Ihnen der Brief (meist in gelbem Umschlag) von der Post zugestellt wurde. Auch wenn Sie zum Beispiel den
                      Brief erst eine Woche nachdem er im Briefkasten lag geöffnet haben, gilt hier aus rechtlicher Sicht immer das Datum der Zustellung. Dieses
                      Datum ist sehr wichtig, denn gegen einen Bußgeldbescheid kann nur innerhalb von zwei Wochen nach Zustellung bei Ihnen Einspruch eingelegt
                      werden. Wird der Einspruch nicht innerhalb der Frist eingelegt, ist er unwirksam und wird von der Behörde als unzulässig verworfen.
                    </p>
                  </DialogModal>
                </Grid>
              </>
            )}
            <Person path="client" showDiverseGenderOption />
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <FormLegend>{translate("traffic.client.birthDate.question", values)}</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorDateField
                name="client.birthDate"
                label={translate("traffic.client.birthDate.label", values)}
                validators={[
                  {
                    validator: isRequired,
                    message: "Bitte geben Sie Ihr Geburtsdatum an",
                  },
                  dateFieldDefault,
                  pastDateDefault,
                ]}
              />
            </Grid>
            <Address path="client.residenceAddress" />
            <Contact />
          </Grid>
          <FormSubmit {...submitProps} />
        </Box>
      </FormFader>
    </Paper>
  );
}
